// src/pages/Brand/components/BrandSearch.js
import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { brandCategories } from '../../../constants/brandCategories';
import { useLanguage } from '../../../contexts/LanguageContext';
import { resolveEnglishBrandQuery } from '../../../utils/brandSearch';
import './BrandSearch.css';

const CATEGORY_EN = {
  '스킨케어': 'Skincare',
  '바디케어': 'Body care',
  '헤어케어': 'Hair care',
  '선케어': 'Sun care',
  '클렌징': 'Cleansing',
};

/**
 * 브랜드 검색 컴포넌트
 * 브랜드 검색 및 자동완성 기능을 제공합니다.
 */
const BrandSearch = ({ currentCategory, onSearch, onReset }) => {
  const { translate, language } = useLanguage();

  // 상태 관리
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [noResults, setNoResults] = useState(false);
  
  // 참조 객체
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  /**
   * 검색어에 맞는 브랜드 필터링
   */
  const filterBrands = (inputValue) => {
    if (!inputValue) {
      setSuggestions([]);
      setNoResults(false);
      return;
    }

    const normalizedInput = inputValue.toLowerCase().replace(/\s+/g, '');

    const matchesCategory = (categories) =>
      currentCategory === '' || categories.some(cat => {
        switch(currentCategory) {
          case 'CM01': return cat === '스킨케어';
          case 'CM02': return cat === '바디케어';
          case 'CM03': return cat === '헤어케어';
          case 'CM04': return cat === '선케어';
          case 'CM05': return cat === '클렌징';
          default: return true;
        }
      });

    const filteredBrands = Object.entries(brandCategories.brands)
      .filter(([brandName, categories]) => {
        const normalizedBrandName = brandName.toLowerCase().replace(/\s+/g, '');
        return normalizedBrandName.includes(normalizedInput) && matchesCategory(categories);
      })
      .slice(0, 10)
      .map(([brandName, categories]) => ({
        name: brandName,
        searchName: brandName,
        categories
      }));

    // 영문 검색어는 공식 영문명 인덱스에서도 후보를 찾는다
    let merged = filteredBrands;
    if (merged.length < 10) {
      const seen = new Set(merged.map(b => b.searchName.toLowerCase().replace(/\s+/g, '')));
      const englishMatches = resolveEnglishBrandQuery(inputValue, 10)
        .filter(c => !seen.has(c.ko.toLowerCase().replace(/\s+/g, '')))
        .map(c => ({
          name: language === 'en' ? c.en : c.ko,
          searchName: c.ko,
          categories: brandCategories.brands[c.ko] || []
        }))
        .filter(c => c.categories.length === 0 ? currentCategory === '' : matchesCategory(c.categories));
      merged = [...merged, ...englishMatches].slice(0, 10);
    }

    setSuggestions(merged);
    setNoResults(merged.length === 0);
  };

  /**
   * 검색 실행
   */
  const handleSearch = () => {
    const normalizedInput = searchTerm.replace(/\s+/g, '');
    const brandExists = Object.keys(brandCategories.brands)
      .some(brandName => brandName.replace(/\s+/g, '').toLowerCase() === normalizedInput.toLowerCase());

    if (brandExists) {
      onSearch(normalizedInput);
      setShowSuggestions(false);
      setNoResults(false);
      return;
    }

    // 영문 브랜드명(공식 표기·과거 오표기)을 한글명으로 변환해 검색
    const candidates = resolveEnglishBrandQuery(searchTerm);
    if (candidates.length > 0) {
      onSearch(candidates[0].ko);
      setShowSuggestions(false);
      setNoResults(false);
    } else {
      setNoResults(true);
    }
  };

  /**
   * 키보드 이벤트 처리
   */
  const handleKeyDown = (e) => {
    if (!showSuggestions) {
      if (e.key === 'Enter' && searchTerm.trim()) {
        e.preventDefault();
        handleSearch();
        return;
      }
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : -1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          setSearchTerm(suggestions[selectedIndex].name);
          setShowSuggestions(false);
          setSelectedIndex(-1);
          handleSearch();
        } else if (searchTerm.trim()) {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  /**
   * 입력값 변경 처리
   */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedIndex(-1);

    if (value.length > 0) {
      filterBrands(value);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  /**
   * 자동완성 항목 클릭 처리
   */
  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.name);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onSearch(suggestion.searchName || suggestion.name);
  };

  /**
   * 검색어 초기화
   */
  const handleClear = () => {
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setNoResults(false);
  };

  /**
   * 검색 초기화
   */
  const handleReset = () => {
    handleClear();
    onReset();
  };

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 카테고리 변경 시 검색어 필터링 업데이트
  useEffect(() => {
    if (searchTerm) {
      filterBrands(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCategory]);

  // 선택된 자동완성 항목 스크롤 처리
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsRef.current) {
      const suggestionsList = suggestionsRef.current;
      const selectedItem = suggestionsList.children[selectedIndex];
      
      if (selectedItem) {
        const itemTop = selectedItem.offsetTop;
        const itemHeight = selectedItem.offsetHeight;
        const containerHeight = suggestionsList.offsetHeight;
        const scrollTop = suggestionsList.scrollTop;

        if (itemTop < scrollTop) {
          suggestionsList.scrollTop = itemTop;
        } else if (itemTop + itemHeight > scrollTop + containerHeight) {
          suggestionsList.scrollTop = itemTop + itemHeight - containerHeight;
        }
      }
    }
  }, [selectedIndex]);

  return (
    <div className="search-container" ref={searchRef}>
      <div className="search-input-wrapper">
        <div className="input-container">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={translate('brandPage.search.placeholder')}
            className="search-input"
          />
          {searchTerm && (
            <button onClick={handleClear} className="clear-button">
              <X size={16} />
            </button>
          )}
        </div>
        <div className="search-buttons">
          <button onClick={handleSearch} className="search-button">
            {translate('brandPage.search.searchButton')}
          </button>
          <button onClick={handleReset} className="reset-button">
            {translate('brandPage.search.resetButton')}
          </button>
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="suggestions-list" ref={suggestionsRef}>
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="suggestion-content">
                <span className="suggestion-name">{suggestion.name}</span>
                <div className="suggestion-categories">
                  {suggestion.categories.map((cat, i) => (
                    <span 
                      key={i} 
                      className={`category-badge ${getCategoryName(currentCategory) === cat ? 'active' : ''}`}
                    >
                      {language === 'en' ? (CATEGORY_EN[cat] || cat) : cat}
                    </span>
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {noResults && (
        <div className="no-results-message">
          {translate('brandPage.search.noResults')}
        </div>
      )}
    </div>
  );
};

/**
 * 카테고리 코드를 카테고리 이름으로 변환
 */
const getCategoryName = (categoryCode) => {
  const categoryMap = {
    'CM01': '스킨케어',
    'CM02': '바디케어',
    'CM03': '헤어케어',
    'CM04': '선케어',
    'CM05': '클렌징',
    '': '전체'
  };
  return categoryMap[categoryCode] || '전체';
};

export default BrandSearch; 